/**
 * Data transformation utilities for Dashboard integration
 * Maps backend API responses to Dashboard component expectations
 */

/**
 * Format tier name for display
 * "free" -> "Free", "enthusiast" -> "Enthusiast", "professional" -> "Professional"
 */
export const formatTierName = (tierName: string): string => {
  if (!tierName) return 'Free'
  return tierName.charAt(0).toUpperCase() + tierName.slice(1)
}

/**
 * Format ISO timestamp to relative time
 * "2025-10-05T23:29:35.179623Z" -> "2 minutes ago"
 */
export const formatTimeAgo = (isoTimestamp: string): string => {
  const now = new Date()
  const timestamp = new Date(isoTimestamp)
  const diffMs = now.getTime() - timestamp.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return 'just now'
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  } else {
    return timestamp.toLocaleDateString()
  }
}

/**
 * Map tool_name from backend to friendly display name
 */
export const getToolDisplayName = (toolName: string): string => {
  const toolMap: Record<string, string> = {
    search_activities: 'Search Activities',
    calculate_standard: 'Calculate Standard LCA',
    calculate_custom: 'Calculate Custom LCA',
    get_task_status: 'Get Calculation Results',
    get_calculation_history: 'Calculation History',
    get_calculation_stats: 'Calculation Statistics',
    search_biosphere: 'Search Biosphere Flows',
    get_biosphere_compartments: 'Biosphere Compartments',
    search_methods: 'Search Impact Methods',
    validate_method: 'Validate Impact Method',
    list_method_categories: 'List Method Categories',
    get_units: 'Get Units',
    validate_unit: 'Validate Unit',
    create_project: 'Create Project',
    list_projects: 'List Projects',
    get_project: 'Get Project',
    update_project: 'Update Project',
    delete_project: 'Delete Project',
    export_project: 'Export Project',
    create_activity: 'Create Activity',
    list_activities: 'List Activities',
    get_activity: 'Get Activity',
    update_activity: 'Update Activity',
    delete_activity: 'Delete Activity',
    read_activity: 'Read Activity Details',
    create_custom_exchange: 'Create Custom Exchange',
    create_database_exchange: 'Create Database Exchange',
    list_exchanges: 'List Exchanges',
    update_exchange: 'Update Exchange',
    delete_exchange: 'Delete Exchange',
    get_graph_schema: 'Get Graph Schema',
    advanced_search: 'Advanced Database Query',
  }

  return (
    toolMap[toolName] ||
    toolName
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ')
  )
}

/**
 * Map status code to success/error state
 */
export const getStatusType = (statusCode: number): 'success' | 'error' => {
  return statusCode >= 200 && statusCode < 300 ? 'success' : 'error'
}

/**
 * Format result message from API log
 */
export const formatResult = (log: {
  tool_name?: string
  method: string
  status_code: number
  response_time_ms?: number
  success?: boolean
}): string => {
  const status =
    log.success !== undefined ? (log.success ? 'success' : 'error') : getStatusType(log.status_code)

  if (status === 'success') {
    if (log.response_time_ms !== undefined) {
      return `completed in ${log.response_time_ms}ms`
    }
    return 'completed successfully'
  } else {
    return `failed (${log.status_code})`
  }
}

/**
 * Map backend call pack data to Dashboard format
 */
export const mapCallPack = (pack: {
  id: number
  price_id?: number
  pack_size: number
  price: number
  name: string
  description?: string
  is_custom: boolean
  paddle_price_id?: string
  product_id?: number
}): {
  id: number
  priceId: string
  name: string
  size: number
  price: number
  description?: string
  popular?: boolean
  paddleId: string
  productId?: number
} => {
  return {
    id: pack.id,
    priceId: pack.paddle_price_id || '', // Use paddle_price_id for client-side checkout
    name: pack.name,
    size: pack.pack_size,
    price: pack.price,
    description: pack.description,
    popular: pack.pack_size === 2000,
    paddleId: pack.paddle_price_id || '', // Keep for reference
    productId: pack.product_id,
  }
}

/**
 * Map backend tool usage log to Dashboard activity format
 */
export const mapActivityLog = (log: {
  tool_name: string
  database_name?: string
  method: string
  status_code: number
  created_at: string
  response_time_ms?: number
  success?: boolean
  request_params?: string
}): {
  time: string
  tool: string
  toolName: string
  database: string
  status: 'success' | 'error'
  result: string
  responseTime: number | undefined
  requestParams: Record<string, unknown> | null
  agent: string
  query: string
} => {
  let parsedParams = null
  if (log.request_params) {
    try {
      parsedParams = JSON.parse(log.request_params)
    } catch {
      parsedParams = null
    }
  }

  return {
    time: formatTimeAgo(log.created_at),
    tool: log.tool_name,
    toolName: getToolDisplayName(log.tool_name),
    database: log.database_name || 'N/A',
    status:
      log.success !== undefined
        ? log.success
          ? 'success'
          : 'error'
        : getStatusType(log.status_code),
    result: formatResult(log),
    responseTime: log.response_time_ms,
    requestParams: parsedParams,
    agent: 'MCP',
    query: log.method || 'N/A',
  }
}

/**
 * Calculate usage percentage
 */
export const calculateUsagePercentage = (currentUsage: number, monthlyLimit: number): number => {
  if (monthlyLimit === 0) return 0
  return Math.min(100, (currentUsage / monthlyLimit) * 100)
}

/**
 * Format date for billing
 * Convert ISO or Date to "MMM DD, YYYY" format
 */
export const formatBillingDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A'

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return 'N/A'
  }
}
